import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";

interface ProductItem {
  label: string;
  url?: string;
}

interface ProductGroup {
  id: string;
  title: string;
  icon: string;
  desc: string;
  items: ProductItem[];
  imageUrl: string;
  links?: { url: string; label: string }[];
}

const GROUPS: ProductGroup[] = [
  {
    id: "servo-valves",
    title: "Servo Valves",
    icon: "ri-equalizer-2-line",
    desc: "MOOG D-Series, G-Series 서보밸브 공급 및 교체·수리 지원",
    items: [
      { label: "D661 Series", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/7b3e94b4-38d1-474f-b182-f837f8e91c2e_Moog-ServoValves-D661_G_A-Catalog-n.pdf" },
      { label: "D651 Series", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/6a212601-2546-47b6-ac39-ae04aa3e895b_D651-seriesvalves.pdf" },
      { label: "D633 / D634 Series", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/1a8a9c9f-998b-4d94-b4a4-5a62d56d98bc_Moog-ServoValves-D633-D634-Catalog-en.pdf" },
      { label: "D936 / D937 Series", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/98e2a36a-fff9-48d0-839f-fd45daff27dd_Moog-ServoPropValves-D93x-Catalog-en.pdf" },
      { label: "G761 Series", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/0474d808-a331-472c-898e-a056d0d3b94d_Moog-ServoValves-761Series-Catalog-en.pdf" },
      { label: "631 Series", url: "https://www.moog.com/products/servovalves-servo-proportional-valves/industrial/servo-valves-without-integrated-electronics/direct-operated-servo-valves-for-analog-signals-631-series.html.html" },
    ],
    imageUrl:
      "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/d3e13c514aa157a01f94703d6b7e94a5.png",
    links: [
      { url: "https://www.moog.com/products/servovalves-servo-proportional-valves/industrial/servo-valves-without-integrated-electronics/direct-operated-servo-valves-for-analog-signals-761-series.html.html", label: "MOOG 공식 제품정보 보기 ↗" },
    ],
  },
  {
    id: "drives-motors",
    title: "Servo Drives & Motors",
    icon: "ri-flashlight-line",
    desc: "MSD 서보드라이브, CD / ExD 서보모터 및 파라미터 셋팅 지원",
    items: [
      { label: "MSD Servo Drive", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/1efe39cc-211c-41f4-9c7c-daac307654ff_MOOG_MSD_Servo_Drive.pdf" },
      { label: "CD Series Servo Motor", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/1e64ba88-c3fb-4f4e-a76a-b3f0d6f43c6d_Moog-ServoMotors-CD_Series-Catalog-en.pdf" },
      { label: "ExD Servo Motor", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/0a4c8e70-a4e4-4aa5-a5e6-5d1c1b5e34cc_Moog-ServoMotors-ExD_Series-Catalog-en.pdf" },
    ],
    imageUrl:
      "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/27bfc92faa10f2b691a8979d7289105d.png",
    links: [
      { url: "https://www.moog.com/products/controllers-drives-software/centralized-servo-drives/MSD-Programmable-Multi-Axis-Servo-Drive-System.html", label: "MOOG 공식 제품정보 보기 ↗" },
      { url: "https://www.moog.com/products/motors-servomotors/Harsh-and-Hazardous-Environment/explosion-proof-dynamic-brushless-servo-motor.html", label: "MOOG 공식 제품정보 보기 ↗" },
    ],
  },
  {
    id: "motion",
    title: "Motion Controllers",
    icon: "ri-cpu-line",
    desc: "MSC III, PLC, EtherCAT, Analog/Digital I/O 모듈 공급",
    items: [
      { label: "MC 600 Motion Controller", url: "https://storage.readdy-site.link/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/d64c593c-a9d5-4eb7-9b60-cc6cd1740eee_Moog-Controllers-MC600-Catalog-en.pdf" },
    ],
    imageUrl:
      "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/ed4374144ac1d875f72f738c126d54c0.png",
    links: [
      { url: "https://www.moog.com/products/controllers-drives-software/industrial-controllers-software/machine-controllers/machine-controllers-series-600.html", label: "MOOG 공식 제품정보 보기 ↗" },
    ],
  },
  {
    id: "sensors",
    title: "Sensors",
    icon: "ri-radar-line",
    desc: "MTS 위치센서, 압력센서 및 케이블·커넥터 공급",
    items: [
      { label: "MTS Position Sensor" },
      { label: "SSI Feedback Sensor" },
      { label: "Analog Feedback Sensor" },
    ],
    imageUrl:
      "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/94a5fd092258b6288d08e522d2f27579.png",
    links: [
      { url: "http://www.imctech.co.kr/product/list.php?cate1=2&cate2=18", label: "MOOG 공식 제품정보 보기 ↗" },
    ],
  },
  {
    id: "hydraulic",
    title: "Hydraulic Components",
    icon: "ri-database-2-line",
    desc: "유압유니트, 매니폴드, 실린더, 부속 밸브 등 통합 공급",
    items: [
      { label: "Hydraulic Unit" },
      { label: "Manifold Block" },
      { label: "Hydraulic Cylinder" },
      { label: "Accumulator" },
      { label: "Cooler" },
      { label: "Relief Valve" },
    ],
    imageUrl:
      "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/5151d8346d3576d0e2fd229e5298588f.png",
  },
];

export default function ProductsPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [location]);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Products"
        title="MOOG 기반 정밀 유압·모션 제어 제품군"
        subtitle="서보밸브 · 서보드라이브 · 모션컨트롤러 · 센서 · 유압 컴포넌트까지 한 곳에서"
        crumbs={[{ label: "HOME", to: "/" }, { label: "Products" }]}
        imageUrl="https://readdy.ai/api/search-image?query=Industrial%20catalog%20overview%20with%20servo%20valves%2C%20motion%20controllers%2C%20hydraulic%20cylinders%20and%20sensors%20arranged%20on%20clean%20light%20gray%20surface%2C%20detailed%20B2B%20product%20photography%2C%20cool%20neutral%20lighting%2C%20editorial%20industrial%20catalog%20composition&width=1920&height=900&seq=mctech-products-hero-01&orientation=landscape"
      />

      {/* Sticky category nav */}
      <section className="sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur border-b border-background-200" data-product-shop>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-2 overflow-x-auto">
          {GROUPS.map((g) => (
            <a
              key={g.id}
              href={`#${g.id}`}
              className="inline-flex items-center gap-2 shrink-0 px-3.5 py-2 rounded-md bg-background-100 hover:bg-primary-100 text-xs md:text-sm font-semibold text-foreground-800 hover:text-primary-800 whitespace-nowrap cursor-pointer"
            >
              <i className={`${g.icon} w-4 h-4 flex items-center justify-center text-primary-600`} />
              {g.title}
            </a>
          ))}
        </div>
      </section>

      {/* Product groups */}
      <section className="py-16 md:py-20 bg-background-50" data-product-shop>
        <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-16 md:space-y-20">
          {GROUPS.map((g, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                key={g.id}
                id={g.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center scroll-mt-32"
              >
                <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative rounded-2xl overflow-hidden border border-background-200 h-72 md:h-96 bg-background-200">
                    <img
                      src={g.imageUrl}
                      alt={g.title}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                  </div>
                  {g.links && g.links.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {g.links.map((link, li) => (
                        <a
                          key={li}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-900 whitespace-nowrap cursor-pointer transition-colors bg-background-100 border border-background-200 rounded-md px-3.5 py-2"
                        >
                          {link.label}
                          <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                      <i className={`${g.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                    </div>
                    <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700">
                      Category {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950 leading-tight">
                    {g.title}
                  </h2>
                  <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                    {g.desc}
                  </p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {g.items.map((it) =>
                      it.url ? (
                        <a
                          key={it.label}
                          href={it.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3.5 py-3 cursor-pointer hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
                        >
                          <i className="ri-external-link-line w-4 h-4 flex items-center justify-center text-primary-600 shrink-0" />
                          <span className="text-sm font-medium text-foreground-900">
                            {it.label}
                          </span>
                          <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-primary-500 ml-auto shrink-0" />
                        </a>
                      ) : (
                        <div
                          key={it.label}
                          className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3.5 py-3"
                        >
                          <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center text-primary-600 shrink-0" />
                          <span className="text-sm font-medium text-foreground-900">
                            {it.label}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTASection
        title="필요한 제품 사양과 모델명을 알려주세요."
        description="MOOG 제품을 포함한 정확한 모델 매칭과 호환 제품 제안까지 가능합니다."
      />
    </SiteLayout>
  );
}